import { useEffect, useState, useCallback } from "react";
import { Share2, Facebook, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";

import { api, API, formatApiError, getShareUrl } from "../lib/api";
import VoteForm from "../components/VoteForm";
import ResultsPanel from "../components/Leaderboard";
import Countdown from "../components/Countdown";
import VoteConfirmation from "../components/VoteConfirmation";
import NoCampaignHero from "../components/NoCampaignHero";
import FloatingVoteNotifier from "../components/FloatingVoteNotifier";
import useVisibilityAwarePolling from "../hooks/useVisibilityAwarePolling";

const LOGO_URL =
  "https://www.ontour.scootive.pl/img/logo.svg";

export default function LandingPage() {
  const [data, setData] = useState({ campaign: null, leaderboard: [], total_votes: 0 });
  const [loaded, setLoaded] = useState(false);
  const [view, setView] = useState("vote");
  const [confirmation, setConfirmation] = useState(null);
  const [votedId, setVotedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.get("/campaign/active");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
    let es;
    try {
      es = new EventSource(`${API}/events`);
      es.addEventListener("vote", () => fetchData());
    } catch {
      /* SSE unsupported — polling fallback below */
    }
    return () => {
      if (es) es.close();
    };
  }, [fetchData]);

  // Visibility-aware fallback polling (refresh when user comes back; light cadence)
  useVisibilityAwarePolling(fetchData, 30000);

  // Page can grow taller than viewport — always allow native scroll on this page
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("allow-scroll");
    return () => root.classList.remove("allow-scroll");
  }, []);

  const handleShare = async (platform) => {
    const url = getShareUrl();
    const text = "Głosuj na Scootive On Tour — zmobilizuj ziomków! 🛴";
    let shareUrl = "";
    if (platform === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    if (platform === "tw") shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    if (platform === "wa") shareUrl = `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`;
    if (platform === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link skopiowany!");
      } catch {
        toast.error("Nie udało się skopiować");
      }
      return;
    }
    window.open(shareUrl, "_blank", "noopener,noreferrer");
  };

  const campaign = data.campaign;
  const optionLabel = campaign?.option_label || "miejsce";
  const resultsTitle = campaign?.results_title || "Ranking";
  const displayMode = campaign?.display_mode === "radio" ? "radio" : "combobox";
  const currentYear = new Date().getFullYear();
  const votingEnded =
    !!campaign?.ends_at && new Date(campaign.ends_at).getTime() <= Date.now();
  const effectiveView = votingEnded ? "results" : view;

  const handleVoted = (option, voteCount) => {
    setConfirmation({ option, votes: voteCount });
    setVotedId(option.id);
    fetchData();
  };

  const handleConfirmationDone = () => {
    setConfirmation(null);
    setView("results");
    fetchData();
  };

  return (
    <div
      className="min-h-screen w-full flex flex-col text-black relative"
      style={{
        background:
          "radial-gradient(110% 60% at 50% -8%, rgba(46,108,46,0.10) 0%, rgba(46,108,46,0.03) 35%, #fafafa 65%)",
      }}
    >
      <div className="w-full max-w-xl mx-auto px-5 sm:px-6 flex-1 flex flex-col items-center text-center">
        {/* Logo top */}
        <div className="shrink-0 pt-8 sm:pt-10 rise-in rise-1 flex flex-col items-center">
          <a
            href="https://www.scootive.pl"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="logo-link"
            className="block"
          >
            <img src={LOGO_URL} alt="Scootive" className="h-9 sm:h-10" />
          </a>
          <p className="mt-2.5 text-[10px] font-medium uppercase tracking-[0.4em] text-black/35">
            On Tour {currentYear}
          </p>
        </div>

        {/* Centered content — flex-1 grows to push footer down; justify-center vertically centers when content fits */}
        <div className="flex-1 w-full flex flex-col items-center justify-center py-10 sm:py-14">
          {!loaded ? (
            <p className="text-[10px] uppercase tracking-[0.3em] text-black/30">Ładowanie…</p>
          ) : !campaign ? (
            <NoCampaignHero />
          ) : (
            <>
              <div className="rise-in rise-2">
                <h1
                  data-testid="hero-headline"
                  className="font-display text-[36px] sm:text-5xl uppercase tracking-tight leading-[0.95]"
                >
                  {campaign.title}
                </h1>
                {campaign.description && (
                  <p className="mt-2.5 text-[13px] sm:text-[14px] text-black/55 leading-relaxed max-w-md mx-auto">
                    {campaign.description}
                  </p>
                )}
              </div>

              {effectiveView === "vote" && (
                <div className="rise-in rise-3 mt-7 sm:mt-8">
                  <Countdown endsAt={campaign.ends_at} />
                </div>
              )}

              <div className="w-full mt-7 sm:mt-8 rise-in rise-4">
                <div className="surface-card p-4 sm:p-5">
                  {effectiveView === "vote" ? (
                    <>
                      <p className="text-[10px] font-medium uppercase tracking-[0.25em] text-black/40 mb-2.5 text-left">
                        Wybierz: <span className="text-brand-green">{optionLabel}</span>
                      </p>
                      <VoteForm
                        optionLabel={optionLabel}
                        hasPostalCodes={!!campaign?.has_postal_codes}
                        displayMode={displayMode}
                        onVoted={handleVoted}
                        onShowResults={() => setView("results")}
                      />
                    </>
                  ) : (
                    <ResultsPanel
                      title={resultsTitle}
                      items={data.leaderboard}
                      totalVotes={data.total_votes}
                      onBack={votingEnded ? undefined : () => setView("vote")}
                      votedId={votedId}
                      votingEnded={votingEnded}
                    />
                  )}
                </div>
              </div>

            </>
          )}

          {/* Share buttons (compact, share goes for active campaign only) */}
          {campaign && (
            <div className="mt-7 rise-in rise-5 flex flex-col items-center">
              <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-black/40 mb-2">
                Zwołaj ziomków
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <button
                  type="button"
                  data-testid="share-fb"
                  onClick={() => handleShare("fb")}
                  className="btn-soft inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-medium"
                >
                  <Facebook className="w-3 h-3" /> Facebook
                </button>
                <button
                  type="button"
                  data-testid="share-tw"
                  onClick={() => handleShare("tw")}
                  className="btn-soft inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-medium"
                >
                  <Share2 className="w-3 h-3" /> X
                </button>
                <button
                  type="button"
                  data-testid="share-wa"
                  onClick={() => handleShare("wa")}
                  className="btn-soft inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-medium"
                >
                  <MessageCircle className="w-3 h-3" /> WhatsApp
                </button>
                <button
                  type="button"
                  data-testid="share-copy"
                  onClick={() => handleShare("copy")}
                  className="btn-brand inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-semibold"
                >
                  <Copy className="w-3 h-3" /> Kopiuj link
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer bottom */}
        <p className="shrink-0 pt-4 pb-4 text-[11px] text-black/35">
          © {currentYear} Scootive
        </p>
      </div>

      {confirmation && (
        <VoteConfirmation
          option={confirmation.option}
          votes={confirmation.votes}
          onDone={handleConfirmationDone}
        />
      )}

      {campaign && <FloatingVoteNotifier variant="apple" />}
    </div>
  );
}
